import React, { useState } from 'react';
import { supabase } from '../../backend/supabaseClient';
import './Login.css';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (isResetPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) throw error;

        setMessage({
          type: 'success',
          text: 'Check your email for the password reset link!'
        });
        setEmail('');
        setIsResetPassword(false);
      } else if (isSignUp) {
        const { data: existingUser } = await supabase
          .from('profiles')
          .select('username')
          .eq('username', username)
          .single();

        if (existingUser) {
          throw new Error('Username already taken');
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              username: username,
              full_name: fullName,
              created_at: new Date().toISOString(),
            }
          }
        });

        if (error) throw error;

        if (data.user) {
          console.log('Creating profile for user:', data.user.id);

          const { error: profileError } = await supabase
            .from('profiles')
            .insert({
              id: data.user.id,
              email: data.user.email,
              username: username,
              full_name: fullName,
              device_settings: {}
            });

          if (profileError) {
            console.error('Error creating profile, trying upsert:', profileError);

            const { error: upsertError } = await supabase
              .from('profiles')
              .upsert({
                id: data.user.id,
                email: data.user.email,
                username: username,
                full_name: fullName,
                device_settings: {}
              }, {
                onConflict: 'id'
              });

            if (upsertError) {
              console.error('Error upserting profile:', upsertError);
            }
          } else {
            console.log('Profile created successfully');
          }
        }

        setMessage({
          type: 'success',
          text: 'Check your email for the confirmation link!'
        });
        setEmail('');
        setPassword('');
        setUsername('');
        setFullName('');
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (mode) => {
    setIsSignUp(mode === 'signup');
    setIsResetPassword(mode === 'reset');
    setMessage({ type: '', text: '' });
    setEmail('');
    setPassword('');
    setUsername('');
    setFullName('');
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>
          {isResetPassword
            ? 'Reset Password'
            : isSignUp
            ? 'Create Account'
            : 'Log in to PointeSense'}
        </h2>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {isSignUp && (
            <>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="name"
                />
              </div>

              <div className="form-group">
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={loading}
                  pattern="[a-zA-Z0-9_-]{3,20}"
                  title="Username must be 3-20 characters and can only contain letters, numbers, underscores, and hyphens"
                  autoComplete="username"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
            />
          </div>

          {!isResetPassword && (
            <div className="form-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength="6"
                disabled={loading}
                autoComplete={isSignUp ? "new-password" : "current-password"}
              />
            </div>
          )}

          <button type="submit" disabled={loading} className="submit-btn">
            {loading
              ? 'Loading...'
              : isResetPassword
              ? 'Send Reset Email'
              : isSignUp
              ? 'Sign Up'
              : 'Login'}
          </button>
        </form>

        <div className="auth-switch">
          {!isResetPassword ? (
            <>
              <p>
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                <button
                  type="button"
                  onClick={() => switchMode(isSignUp ? 'login' : 'signup')}
                  className="switch-btn"
                  disabled={loading}
                >
                  {isSignUp ? 'Login' : 'Sign Up'}
                </button>
              </p>
              {!isSignUp && (
                <p>
                  <button
                    type="button"
                    onClick={() => switchMode('reset')}
                    className="switch-btn forgot-password"
                    disabled={loading}
                  >
                    Forgot Password?
                  </button>
                </p>
              )}
            </>
          ) : (
            <p>
              Remember your password?
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="switch-btn"
                disabled={loading}
              >
                Back to Login
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
